import { Injectable } from '@angular/core';
import { isNil, Nillable } from '@web/utils';
import { AlertSeverity } from '../enums/alert-severity';
import { Alert } from '../models/alert';
import { RequiredMethodCallException } from '@web/common';

@Injectable({
  providedIn: 'root',
})
export class AlertBuilder {
  private _title: Nillable<string>;
  private _content: Nillable<string>;
  private _severity: Nillable<AlertSeverity>;
  private _closeable: Nillable<boolean>;

  public constructor() {
    this.reset();
  }

  public newInstance(): AlertBuilder {
    return new AlertBuilder();
  }

  public reset(): this {
    this._title = null;
    this._content = null;
    this._severity = null;
    this._closeable = null;

    return this;
  }

  public buildSuccess(content: string): Alert {
    return this.initSuccess(content).build();
  }

  public buildFailure(content: string): Alert {
    return this.initFailure(content).build();
  }

  public buildWarning(content: string): Alert {
    return this.initWarning(content).build();
  }

  public buildInfo(content: string): Alert {
    return this.initInfo(content).build();
  }

  public initSuccess(content: string): this {
    return this.severity(AlertSeverity.Success).content(content);
  }

  public initWarning(content: string): this {
    return this.severity(AlertSeverity.Warning).content(content);
  }

  public initFailure(content: string): this {
    return this.severity(AlertSeverity.Failure).content(content);
  }

  public initInfo(content: string): this {
    return this.severity(AlertSeverity.Info)
      .content(content)
      .title('Komunikat');
  }

  public content(content: string): this {
    this._content = content;

    return this;
  }

  public title(title: string): this {
    this._title = title;

    return this;
  }

  public severity(severity: AlertSeverity): this {
    this._severity = severity;

    return this;
  }

  public closeable(closeable: boolean): this {
    this._closeable = closeable;

    return this;
  }

  public build(): Alert {
    this.requireContent();
    this.requireSeverity();

    const alert = new Alert(
      this._title!,
      this._content!,
      this._severity!,
      this._closeable!,
    );

    this.reset();

    return alert;
  }

  private requireContent(): void {
    if (!isNil(this._content)) {
      return;
    }

    throw new RequiredMethodCallException(this.content.name);
  }

  private requireSeverity(): void {
    if (!isNil(this._severity)) {
      return;
    }

    throw new RequiredMethodCallException(this.severity.name);
  }
}
